import { selectors } from "../../../../add-element/cypressSelectors";
import { TrackContext } from "../../../../contexts/TrackContext";
import { useVisibleGuestList } from "../../../../contexts/VisibleGuestListContext";
import API from "../../../../core/api";
import { getGuestAuthSettings } from "../../../../core/utils";
import { ATTENDING, AWAITING_REPLY, MAYBE } from "../../../../guests/constants";
import { isEmailValid } from "../../../../shared/validations/emailValidations";
import { themeTypographyResetStyles } from "../../../../website/components/themeTypographyResetStyles";
import { scrollToElement } from "../../utils";
import EventDetails from "../EventDetails";
import {
  QUESTION_WITH_ERROR_CLASSNAME,
  RSVP_LOCAL_STORAGE_KEY,
} from "../constants";
import type {
  ContactData,
  Guest,
  GuestResponse,
  RSVPContact,
  RSVPEvent,
} from "../types";
import OnlineInvitationEventGuest from "./OnlineInvitationEventGuest";
import OnlineInvitationEventsGuestsSummary from "./OnlineInvitationEventsGuestsSummary";
import { css } from "@emotion/react";
import {
  Alert,
  Button,
  theme,
  styleUtils,
  useMediaQueryState,
} from "@minted/minted-components";
import isEmpty from "lodash/isEmpty";
import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
} from "react";

type Props = {
  contact: RSVPContact;
  event: RSVPEvent;
  guest: Guest;
};

type RSVPLocalStorageState = {
  adultCount: number;
  childCount: number;
  contactData: ContactData;
  event: RSVPEvent;
  guest: Guest;
  guestResponse: GuestResponse;
  showSummary: boolean;
};

type InitialFormData = {
  adultCount: number;
  childCount: number;
  contactData: ContactData;
  guestId: number;
  guestResponse: GuestResponse;
  showSummary: boolean;
};

const rsvpStyles = {
  buttonsWrapper: css`
    margin-bottom: ${theme.spacing.x5};
    display: flex;
    gap: ${theme.spacing.x4};
  `,
  buttonWrapper: css`
    display: flex;
    margin-top: ${theme.spacing.x4};
    margin-bottom: ${theme.spacing.x4};
  `,
  emailMargins: css`
    margin-left: ${theme.spacing.x5};
    margin-right: ${theme.spacing.x5};
    margin-bottom: ${theme.spacing.x5};
  `,
  formWrapper: css`
    background-color: ${theme.colors.gray200};
    display: grid;
    gap: ${theme.spacing.x5};
    padding: ${theme.spacing.x5};
    ${theme.media.greaterThan("small")`
      padding: ${theme.spacing.x4};
    `};
  `,
  partySizeLabel: css`
    min-width: ${styleUtils.rem(100)};
  `,
  partySizeWrapper: css`
    display: flex;
    flex-direction: row;
    align-items: center;
  `,
  wrapper: css`
    background-color: ${theme.colors.white};
    color: ${theme.colors.textPrimary};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.x4};
    margin-top: ${theme.spacing.x5};
    margin-left: auto;
    margin-right: auto;
    max-width: ${styleUtils.rem(660)};
    text-align: initial;
    width: 100%;
  `,
};

const DESKTOP_NAVBAR_OFFSET = 54;
const MOBILE_NAVBAR_OFFSET = 44;

// Stored state is stale if the freshly fetched event or guest no longer
// matches what was saved (host changed the event, or a different guest
// matched this search than last time).
const isStoredStateStale = (
  stored: RSVPLocalStorageState,
  event: RSVPEvent,
  guest: Guest,
) => stored.event?.id !== event.id || stored.guest?.id !== guest.id;

// Read and validate stored contact RSVP state
export const getStoredFormState = (
  contactId: string | null,
  event: RSVPEvent,
  guest: Guest,
): RSVPLocalStorageState | null => {
  const stored = localStorage.getItem(RSVP_LOCAL_STORAGE_KEY);

  if (stored) {
    const parsed = JSON.parse(stored) as RSVPLocalStorageState;

    if (
      parsed?.contactData?.id === contactId &&
      !isStoredStateStale(parsed, event, guest)
    ) {
      return parsed;
    }
  }
  return null;
};

const getInitialData = (
  contact: RSVPContact,
  event: RSVPEvent,
  guest: Guest,
): InitialFormData => {
  const stored = getStoredFormState(contact.id, event, guest);
  const maxPartySize = guest.maxParty || 1;

  return {
    adultCount:
      stored?.adultCount ??
      (guest.adultCount || (contact.id ? maxPartySize : 1)),
    childCount: stored?.childCount ?? (guest.childCount || 0),
    contactData: stored?.contactData ?? contact,
    guestId: stored?.guest.id ?? guest.id,
    guestResponse: stored?.guestResponse ?? {
      answers: {},
      responseType: AWAITING_REPLY,
    },
    showSummary: stored?.showSummary ?? false,
  };
};

const OnlineInvitationRSVPForm = ({ contact, event, guest }: Props) => {
  const separateAdultChild = Boolean(event?.adultChildRsvpsEnabled);
  const emitSiteEvent = useContext(TrackContext);
  const { refresh: refreshGuestList } = useVisibleGuestList();
  const [initialData] = useState(() => getInitialData(contact, event, guest));
  const [guestResponse, setGuestResponse] = useState(initialData.guestResponse);
  // Tracking this because it may be temporary if the invite is open
  const [guestId, setGuestId] = useState(initialData.guestId);
  const [contactData, setContactData] = useState(initialData.contactData);
  const [showSummary, setShowSummary] = useState(initialData.showSummary);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [validateFields, setValidateFields] = useState(false);
  const [isResponseChanged, setIsResponseChanged] = useState(false);
  const maxPartySize = guest.maxParty || 1;
  const [adultCount, setAdultCount] = useState(initialData.adultCount);
  const [childCount, setChildCount] = useState(initialData.childCount);

  const wrapper = useRef<HTMLDivElement>(null);
  const mediumMediaQueryState = useMediaQueryState({
    mediaQuerySize: "medium",
  });
  const isMobile = mediumMediaQueryState === "BELOW";

  // used to automatic scroll on errors or next/submit click
  const navbarOffset = isMobile ? MOBILE_NAVBAR_OFFSET : DESKTOP_NAVBAR_OFFSET;

  const isFormComplete = useMemo(() => {
    if (!contactData.id) {
      if (
        // Name is required
        !contactData.name ||
        // Email is required if attending or maybe
        ((guestResponse.responseType === ATTENDING ||
          guestResponse.responseType === MAYBE) &&
          !contactData.email) ||
        // If email is valued, it must be valid
        (contactData.email && !isEmailValid(contactData.email))
      ) {
        return false;
      }
    }
    const requiredQuestions = event.questions.filter(
      (question) => question.required,
    );

    if (guestResponse.responseType === AWAITING_REPLY) {
      return false;
    } else if (guestResponse.responseType === ATTENDING) {
      if (separateAdultChild && adultCount + childCount < 1) {
        return false;
      }

      // All required questions must be answered
    } else if (
      guestResponse.responseType === ATTENDING ||
      guestResponse.responseType === MAYBE
    ) {
      // All required questions must be answered for Yes and Maybe
      for (const question of requiredQuestions) {
        if (!guestResponse.answers[question.id]) {
          return false;
        }
      }
    }

    return true;
  }, [
    adultCount,
    childCount,
    contactData.id,
    contactData.name,
    contactData.email,
    event.questions,
    guestResponse.responseType,
    guestResponse.answers,
    separateAdultChild,
  ]);

  const updateGuestResponse = useCallback((newResponse: GuestResponse) => {
    setGuestResponse(newResponse);

    setValidateFields(false);
  }, []);

  const updateContactData = useCallback(
    (fieldName: string, newValue: string) => {
      // Passing in data like {name: "New contact name"}
      setContactData((prevContactData) => ({
        ...prevContactData,
        [fieldName]: newValue,
      }));
      setValidateFields(false);
    },
    [],
  );

  const effectivePartySize = adultCount + childCount;
  const hasInvalidAttendingPartySize =
    separateAdultChild &&
    guestResponse.responseType === ATTENDING &&
    effectivePartySize < 1;

  const incrementAdultCount = useCallback(() => {
    setAdultCount((prev) =>
      prev + childCount < maxPartySize ? prev + 1 : prev,
    );
  }, [childCount, maxPartySize]);

  const decrementAdultCount = useCallback(() => {
    setAdultCount((prev) => Math.max(0, prev - 1));
  }, []);

  const incrementChildCount = useCallback(() => {
    setChildCount((prev) =>
      adultCount + prev < maxPartySize ? prev + 1 : prev,
    );
  }, [adultCount, maxPartySize]);

  const decrementChildCount = useCallback(() => {
    setChildCount((prev) => Math.max(0, prev - 1));
  }, []);

  const submitRsvp = useCallback(() => {
    if (!isFormComplete) {
      setValidateFields(true);
      setTimeout(() => {
        const wrappersWithErrors = document.querySelectorAll(
          `.${QUESTION_WITH_ERROR_CLASSNAME}`,
        );

        if (!isEmpty(wrappersWithErrors)) {
          scrollToElement(wrappersWithErrors[0], {
            offset: navbarOffset,
          });
        }
      }, 1);

      return null;
    }

    emitSiteEvent({
      event: "click_submit_RSVP",
      feature: "open_invite_online_invitations",
      is_response_changed: isResponseChanged,
    });

    // Flatten answers object to list
    const answers: { answer: string; questionId: string }[] = [];

    for (const [questionId, answer] of Object.entries(guestResponse.answers)) {
      answers.push({
        answer,
        questionId,
      });
    }
    const requestData = {
      addressbookContactId: contactData.id,
      answers,
      eventId: event.id,
      // Guest ID is set to 1 for form state management, un-set that
      // on RSVP submit so the backend knows to create a new guest
      guestId: contactData.id ? guestId : null,
      // Contact name has to be valued for open invitations, email does not
      newContactEmail: contactData.email,
      newContactName: contactData.name,
      // OI always uses adult_count (and child_count when adult/child enabled). Never send party.
      adultCount: separateAdultChild ? adultCount : Math.max(1, adultCount),
      childCount: separateAdultChild ? childCount : 0,
      responseType: guestResponse.responseType,
    };

    setIsSubmitting(true);
    API.post(
      "guest-management/rsvp-online-invitation",
      getGuestAuthSettings(),
      requestData,
    )
      .then((responseData: { contactId: string; guestId: number }) => {
        // New contact and guest were created, make sure to use those for updates later
        const finalContactData = requestData.addressbookContactId
          ? contactData
          : { ...contactData, id: responseData.contactId };
        const finalGuest = requestData.addressbookContactId
          ? guest
          : { ...guest, id: responseData.guestId };

        // Open invite, set the created contact info and guest ID for the frontend
        if (!requestData.addressbookContactId) {
          setContactData(finalContactData);
          setGuestId(finalGuest.id);
        }

        localStorage.setItem(
          RSVP_LOCAL_STORAGE_KEY,
          JSON.stringify({
            adultCount,
            childCount,
            contactData: finalContactData,
            event,
            guest: finalGuest,
            guestResponse,
            showSummary: true,
          }),
        );

        setShowSummary(true);

        // Trigger a force refresh of the guest list display
        refreshGuestList(true);

        scrollToElement(wrapper.current, {
          offset: navbarOffset,
        });
      })
      .finally(() => setIsSubmitting(false));
  }, [
    adultCount,
    childCount,
    isFormComplete,
    contactData,
    emitSiteEvent,
    event,
    guest,
    guestId,
    isResponseChanged,
    guestResponse,
    navbarOffset,
    refreshGuestList,
    separateAdultChild,
  ]);

  const onChangeResponse = () => {
    emitSiteEvent({
      event: "click_change_response",
      feature: "open_invite_online_invitations",
    });
    setIsResponseChanged(true);
    setCurrentEventIndex(0);
    setShowSummary(false);
  };

  useEffect(() => {
    scrollToElement(wrapper.current, {
      offset: navbarOffset,
    });
  }, [currentEventIndex, navbarOffset]);

  return (
    <div
      css={[rsvpStyles.wrapper, themeTypographyResetStyles]}
      data-cy="rsvpForm"
      ref={wrapper}
    >
      {showSummary ? (
        <div>
          <OnlineInvitationEventsGuestsSummary
            contactData={contactData}
            event={event}
            guest={guest}
            guestResponse={guestResponse}
            isMobile={isMobile}
            partySize={effectivePartySize}
          />
          <div css={rsvpStyles.buttonWrapper}>
            <Button
              dataAttrs={[["cy", selectors.oi_rsvp_changeResponseButton]]}
              expand
              onClick={onChangeResponse}
              size="large"
              text="Change Response"
              type="primary"
            />
          </div>
        </div>
      ) : (
        <>
          <EventDetails
            calendarUrls={event.calendarUrls}
            event={event}
            index={currentEventIndex}
            isMobile={isMobile}
            totalEvents={1}
          />

          <div css={rsvpStyles.formWrapper}>
            <OnlineInvitationEventGuest
              adultCount={adultCount}
              childCount={childCount}
              contactData={contactData}
              decrementAdultCount={decrementAdultCount}
              decrementChildCount={decrementChildCount}
              event={event}
              guest={guest}
              guestResponse={guestResponse}
              incrementAdultCount={incrementAdultCount}
              incrementChildCount={incrementChildCount}
              maxPartySize={maxPartySize}
              partySize={effectivePartySize}
              questions={event.questions}
              separateAdultChild={separateAdultChild}
              updateContactData={updateContactData}
              updateGuestResponse={updateGuestResponse}
              validateFields={validateFields}
            />
          </div>
          <Alert
            primaryText="Please add at least 1 adult or child guest to continue."
            show={hasInvalidAttendingPartySize}
            type="negative"
          />
          <Button
            dataAttrs={[["cy", selectors.rsvp_submitButton]]}
            disabled={isSubmitting || hasInvalidAttendingPartySize}
            expand
            onClick={submitRsvp}
            size="large"
            text="Submit"
            type="primary"
          />
        </>
      )}
    </div>
  );
};

export default OnlineInvitationRSVPForm;
